import {
  Circle,
  Grid,
  GridItem,
  Heading,
  HStack,
  Spinner,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { BackButton, TouchableOpacity } from "components";
import { getSession } from "features/auth/slice";
import { useTranslation } from "languages";
import React from "react";
import { useNavigate } from "react-router-dom";
import { AppLinks } from "tools/navigations";
import { useAppDispatch, useAppSelector } from "tools/store";
import { createAyahNumber, timeCalculator } from "utils/Helpers";
import { getIsArabic } from "../global_slice";
import { setCurrentPage, setSelected } from "../non_persist_slice";
import { useDeleteNoteMutation, useGetNotesQuery } from "../service";

const Notes: React.FC = () => {
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const translation = useTranslation();
  const isArabic = useAppSelector(getIsArabic);
  const userData = useAppSelector(getSession);

  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();

  const { data, isLoading } = useGetNotesQuery(userData?.user?.id || 0, {
    skip: !userData?.user?.id,
  });
  const list =
    data && data?.length > 0
      ? data.map((it: any) => ({ ...it, content: JSON.parse(it?.content) }))
      : [];

  const handleRemoveItem = (index: number) => {
    deleteNote(index);
  };

  const layoutDirection = useBreakpointValue({ base: "column", md: "row" });

  return (
    <Stack w={"100%"} h={"100%"}>
      <BackButton />

      <Heading px={[4, 10]} pt={2}>
        {translation["notes"]}
      </Heading>

      {(isLoading || isDeleting) && (
        <Spinner size="xl" position={"absolute"} top={"50%"} left={"50%"} />
      )}

      <Stack
        w={"100%"}
        px={[4, 10]}
        pt={[4, 10]}
        pb={[40, 10]}
        overflowY={"scroll"}
        className="mac-scrollbar"
      >
        {!isLoading && list.length === 0 && (
          <Heading
            position={"absolute"}
            top={"50%"}
            left={layoutDirection === "column" ? undefined : "50%"}
          >
            {translation["empty_data"]}
          </Heading>
        )}

        <Grid
          templateColumns={`repeat(${
            layoutDirection === "column" ? "1" : "3"
          }, 1fr)`}
          gap={[2, 6]}
          w={"100%"}
        >
          {list.map((it: any, index: number) => (
            <GridItem
              w="100%"
              key={index}
              bgColor={"secondary.300"}
              p={3}
              rounded={"xl"}
            >
              <TouchableOpacity
                gap={5}
                onClick={() => {
                  dispatch(setCurrentPage(it?.content?.page));
                  dispatch(setSelected(it?.content));
                  navigation(AppLinks.DASHBOARD);
                }}
              >
                <HStack w={"100%"} justify={"space-between"}>
                  <Text color={"primary.300"}>
                    {isArabic
                      ? it?.content?.surah?.name
                      : it?.content?.surah?.englishName}{" "}
                    {isArabic
                      ? createAyahNumber(it?.content?.surah?.number)
                      : it?.content?.surah?.number}
                  </Text>
                  <Circle size="35px" bg="secondary.350">
                    <Text>
                      {isArabic ? createAyahNumber(index + 1) : index + 1}
                    </Text>
                  </Circle>
                </HStack>
                <Text noOfLines={1} color={"white"} fontSize={"lg"}>
                  {it?.content?.note}
                </Text>

                <HStack w={"100%"} justify={"space-between"}>
                  <Text color={"secondary.700"} fontSize={"sm"}>
                    {timeCalculator(new Date(it?.content?.date))}
                  </Text>
                  <HStack gap={4}>
                    <TouchableOpacity
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(it?.id);
                      }}
                    >
                      <Text fontSize={"sm"} as={"u"} color={"red"}>
                        {translation["remove"]}
                      </Text>
                    </TouchableOpacity>
                    <svg
                      id="edit"
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 30 30"
                    >
                      <g id="vuesax_bold_edit" data-name="vuesax/bold/edit">
                        <g id="edit-2" data-name="edit">
                          <path
                            id="Vector"
                            d="M17.726,25H7.263C2.715,25,0,22.28,0,17.726V7.263C0,2.715,2.715,0,7.263,0H17.738a7.331,7.331,0,0,1,5.338,1.928,7.32,7.32,0,0,1,1.911,5.335V17.738C24.987,22.285,22.273,25,17.726,25ZM11.213,8.25h0L5.637,13.825a3.05,3.05,0,0,0-.787,1.55L4.412,18.45A1.9,1.9,0,0,0,4.9,20.125a1.848,1.848,0,0,0,1.324.512,3.113,3.113,0,0,0,.338-.025l3.076-.438a3.181,3.181,0,0,0,1.549-.788l5.6-5.562c-.255-.085-.508-.193-.812-.325l-.017-.009c-.265-.132-.538-.269-.783-.417a4.645,4.645,0,0,1-.613-.4c-.16-.111-.348-.262-.529-.407l-.007-.006a.855.855,0,0,1-.135-.122l-.028-.028a8.227,8.227,0,0,1-.988-.988,1.8,1.8,0,0,1-.137-.175,4.588,4.588,0,0,1-.4-.524,6.332,6.332,0,0,1-.376-.562c-.161-.27-.3-.563-.412-.788-.135-.292-.233-.552-.329-.8l-.008-.022Zm5.225-3.86A4.005,4.005,0,0,0,13.65,5.8L12.488,6.963a.37.37,0,0,0-.1.375,7.755,7.755,0,0,0,5.288,5.287.314.314,0,0,0,.112.012.386.386,0,0,0,.276-.113l1.15-1.162a4.052,4.052,0,0,0,1.424-2.75A4.024,4.024,0,0,0,19.225,5.8,4,4,0,0,0,16.438,4.389Z"
                            transform="translate(2.5 2.5)"
                            fill="#bf8d46"
                          />
                          <path
                            id="Vector-2"
                            data-name="Vector"
                            d="M30,0V30H0V0Z"
                            transform="translate(30 30) rotate(180)"
                            fill="#bf8d46"
                            opacity="0"
                          />
                        </g>
                      </g>
                    </svg>
                  </HStack>
                </HStack>
              </TouchableOpacity>
            </GridItem>
          ))}
        </Grid>
      </Stack>
    </Stack>
  );
};

export default Notes;
